// ── Project (shared by Pre-Sales and Delivery) ─────────────────────────────────
// A single Project entity spans both domains, differentiated by `phase`. See
// requirements/projects/projects_feature_spec.md §2–§8. `status` is a workflow step *name*
// (validated server-side against the project's WorkflowDefinition — see workflow.types.ts),
// not a fixed union, because step lists must be admin-configurable (§3).

import type { IUserRef } from './common.types';

export type ProjectPhase = 'presales' | 'delivery';

export type ProjectPriority = 'Critical' | 'High' | 'Medium' | 'Low';

/** Fixed-enum industry segment — classifies the customer/account archetype for a project. */
export type IndustrySegment =
  | 'Digital Native Business (DNB)'
  | 'Enterprise'
  | 'GCC'
  | 'ISV / SaaS'
  | 'ITES'
  | 'Mid Market'
  | 'Other'
  | 'SMB'
  | 'Startup';

/** All valid IndustrySegment values, in display order. Use this for dropdowns and `@IsIn` validators. */
export const INDUSTRY_SEGMENTS: IndustrySegment[] = [
  'Digital Native Business (DNB)',
  'Enterprise',
  'GCC',
  'ISV / SaaS',
  'ITES',
  'Mid Market',
  'Other',
  'SMB',
  'Startup',
];

/** A monetary value with its own currency (§1) — no cross-currency conversion. Amount is
 *  integer cents in `currency`'s unit. */
export interface IMoney {
  amountCents: number;
  currencyId: string;
  currency?: { id: string; code: string; symbol: string };
}

export type TatComplianceStatus = 'compliant' | 'breached' | 'pending' | 'none';

/** A dated staffing record for a user on a project, enriched with capacity data for Resource
 *  Levelling. A person may have multiple non-overlapping records on the same project over time
 *  (see `staffingId`) — `id` (from `IUserRef`) stays the *user's* id throughout. Only
 *  *currently-active* records appear on `IProject.staffing`; the full active + past list is
 *  available via `GET /projects/:id/staffing/history`, which uses this same shape.
 *  `weeklyCapacityHours` is the user's global default; `allocatedHoursPerWeek` is an
 *  optional per-project override (null/undefined means "inherit the global default") — this
 *  lets one person's time be split realistically across multiple projects. */
export interface IProjectStaffingMember extends IUserRef {
  /** The staffing record's own id — not the user's id — needed to act on this specific record
   *  (e.g. destaff), since a user may have multiple records over time. */
  staffingId: string;
  weeklyCapacityHours: number;
  allocatedHoursPerWeek?: number | null;
  /** ISO 8601 date — this staffing record's Start Date. */
  startDate: string;
  /** ISO 8601 date — this staffing record's End Date; null/undefined means ongoing (not yet
   *  destaffed). */
  endDate?: string | null;
  /** True when today falls within [startDate, endDate ?? unbounded]. Always true for entries in
   *  IProject.staffing (already filtered); meaningful on the Staffing History list. */
  isActive: boolean;
}

export interface IProject {
  id: string;
  /** Determines which domain module owns this record's lifecycle and which workflow applies. */
  phase: ProjectPhase;

  // 5a. Basic Information
  name: string;
  /** Auto-generated from name, overridable at creation, immutable once saved for the first
   *  time (§5a). */
  projectKey: string;
  accountId: string;
  account?: { id: string; name: string };
  businessUnitId: string;
  businessUnit?: { id: string; name: string };
  priority?: ProjectPriority;
  /** Industry segment — classifies the customer/account archetype. Fixed enum, optional. */
  industrySegment?: IndustrySegment;
  /** Markdown. */
  description?: string;
  projectTypeId: string;
  projectType?: { id: string; name: string };
 
  fundingTypeId?: string;
  fundingType?: { id: string; name: string };
  /** ISO 8601 date. */
  startDate: string;
  /** ISO 8601 date. */
  targetDate?: string;
  /** ISO 8601 date. */
  endDate?: string;
  documentFolderLink?: string;

  // 5b. Staffing
  projectLeadId: string;
  projectLead?: IUserRef;
  /** Only present/settable when the `SHOW_PROJECT_MANAGER_FIELD` toggle is on (§5b). Nullable,
   *  unlike projectLeadId — a project need not have a Project Manager. */
  projectManagerId?: string;
  projectManager?: IUserRef;
  staffing: IProjectStaffingMember[];

  // 5c. Client Information — moved to a one-to-many sub-resource, see IProjectClientContact and
  // `GET/POST/PATCH /projects/:projectId/client-contacts`.

  // 5d. Contractual & Finance — restricted (§14c). Omitted from API responses entirely
  // (not just hidden client-side) for callers without `project_finance:read`.
  sowAttachmentId?: string;
  pocFundingStatusId?: string;
  pocFundingStatus?: { id: string; name: string };
  productionFundingStatusId?: string;
  productionFundingStatus?: { id: string; name: string };
  targetRevenue?: IMoney;
  /** Kept in the API/DB for existing data — deliberately not shown in the Contractual & Finance
   *  UI any more (superseded there by `approvedBudget`). */
  targetProfitability?: IMoney;
  awsMrr?: IMoney;
  approvedBudget?: IMoney;
  /** Delivery Projects exemption flow — Project-Lead-set, independent of the funding-status
   *  dropdowns above. Gates the Pre-Requisites Awaited -> Kick Off transition. */
  isFunded?: boolean;

  // §2 — Delivery projects may reference the Pre-Sales project that led to them. Reference
  // link only; no data carries over.
  presalesProjectId?: string;
  presalesProject?: { id: string; name: string; projectKey: string };

  // Workflow (§3)
  status: string;
  /** Stamped when a presales project first reaches the "Submitted" step — drives TAT
   *  compliance (§4). */
  submittedAt?: string;

  // TAT (§4) — presales phase only, resolved + computed on read, never stored directly other
  // than the resolved turnAroundTimeId snapshot.
  turnAroundTimeId?: string;
  turnAroundTime?: { id: string; name: string; valueDays: number };
  /** ISO 8601 date — startDate + TAT valueDays. Absent if no TAT row matched (§4 "no-match"). */
  tatDeadline?: string;
  tatCompliance?: TatComplianceStatus;

  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

// §18 — the Project Detail page's tabs, grouped two levels deep. Single source of truth for
// the tab set: consumed by the tab UI itself, the admin default-landing-tab config screen, and
// the workflow-step DTO's validator (a step's defaultTabName must be one of these leaf names).
// 'Gantt' deliberately appears in two groups — it's the same screen either way (ProjectGanttTab
// keys off `activeTab === 'Gantt'` alone, not which group is selected), just reachable from
// Planning (next to Planner) as well as Execution (next to Board) since both are natural places
// to look for it.
export const PROJECT_DETAIL_TAB_GROUPS = {
  Setup: ['Overview', 'Client Contacts', 'Notes', 'Staffing', 'Pre-Requisites', 'Milestones', 'Holidays'],
  Planning: ['Sprints', 'Modules', 'Issues', 'Planner', 'Gantt', 'Resource Levelling'],
  Execution: ['Board', 'Gantt', 'Risks', 'Dependencies', 'Escalations'],
  'Change Management': ['Change Requests'],
  // A whole top-level group, not a leaf tab under Setup — Burn Rate is the first Finance tab;
  // more (profitability, ETC, budget) are expected to join it here later.
  Finance: ['Burn Rate'],
} as const;
export type ProjectTabGroupName = keyof typeof PROJECT_DETAIL_TAB_GROUPS;
export type ProjectDetailTabName = (typeof PROJECT_DETAIL_TAB_GROUPS)[ProjectTabGroupName][number];
// Deduped — 'Gantt' is a member of two groups above (see its own comment), but this flat list
// feeds validation (`@IsIn`) and any UI that just needs "every valid leaf tab name once," where
// a duplicate would be redundant at best (harmless for IsIn) and a stray React duplicate-key
// warning at worst (see ProjectTabDefaultsPage.tsx, which iterates the grouped object directly
// instead, precisely so 'Gantt' still shows once per group there).
export const PROJECT_DETAIL_TAB_NAMES = [...new Set(Object.values(PROJECT_DETAIL_TAB_GROUPS).flat())];

export interface IProjectNote {
  id: string;
  projectId: string;
  /** Markdown. */
  body: string;
  author: IUserRef;
  createdAt: string;
  updatedAt: string;
}

// A project's pre-requisite checklist — one row per ProjectPrerequisite configured for the
// project's ProjectType (§7), with the project's own checked/note state layered on.
export interface IProjectPrerequisiteStatus {
  prerequisiteId: string;
  name: string;
  description?: string | null;
  checked: boolean;
  /** Optional note, addable once checked (§7 — source typo "not" read as "note"). */
  note?: string | null;
  /** ISO 8601 timestamp — stamped only on the false → true transition, not on later note
   *  edits; cleared when unchecked. */
  checkedAt?: string;
  checkedBy?: IUserRef;
}

export interface IProjectModule {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  isActive: boolean;
}

// Client Contacts tab — a project-scoped client contact. Multiple per project. `clientName`,
// `clientEmail`, and `role` are mandatory (there's no legacy migrated data to accommodate);
// `clientPhone` is the one genuinely optional field.
export interface IProjectClientContact {
  id: string;
  projectId: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  role: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

// Setup > Holidays — a project-scoped non-working day. Applies only to this project (not an
// org-wide calendar); consumed by Resource Levelling (demand distribution skips these dates the
// same way it skips weekends) and Gantt (shaded on the timeline alongside weekends).
export interface IProjectHoliday {
  id: string;
  projectId: string;
  /** ISO date (YYYY-MM-DD). */
  date: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  isActive: boolean;
}

// A single issue's actual work-log contribution to an IResourceWorkloadDay's total — feeds the
// click-to-see-breakdown panel. `projectId`/`projectKey` identify the source project in the
// "All Projects" scope.
export interface IResourceWorkloadIssueContribution {
  issueId: string;
  issueKey: string;
  issueName: string;
  projectId: string;
  projectKey: string;
  hours: number;
}

export interface IResourceWorkloadMember extends IUserRef {
  weeklyCapacityHours: number;
}

export type ResourceWorkloadScope = 'project' | 'all';

// Resource Levelling actual hours, read from issue work logs. The member list is people who
// have logged time on the project being viewed; "All Projects" sums those same members' logs
// across projects. Sparse — only (userId, date) pairs with hours > 0 are included.
export interface IResourceWorkloadDay {
  userId: string;
  /** ISO date (YYYY-MM-DD). */
  date: string;
  hours: number;
  /** Sorted by hours descending. */
  items: IResourceWorkloadIssueContribution[];
}

export interface IResourceWorkloadResponse {
  from: string;
  to: string;
  members: IResourceWorkloadMember[];
  days: IResourceWorkloadDay[];
}
